import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getEventById, createEvent, updateEvent } from '../../services/apiClient';
import type { Event, EventStatus, EventType } from '../../types';
import { slugify } from '../../lib/formatting';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import styles from './EditEvent.module.css';

const EMPTY_FORM: Omit<Event, 'id' | 'organization_id'> = {
  title: '',
  slug: '',
  type: 'tournament',
  start_date: '',
  end_date: '',
  location: '',
  description: '',
  registration_open: false,
  registration_deadline: '',
  max_entries: 0,
  status: 'draft',
  featured_image_url: '',
};

const EVENT_TYPES: EventType[] = ['tournament', 'meeting', 'fundraiser', 'social', 'workshop', 'other'];
const EVENT_STATUSES: EventStatus[] = ['draft', 'published', 'cancelled', 'completed'];

export function EditEvent() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const isNew = !eventId || eventId === 'new';

  const [form, setForm] = useState<Omit<Event, 'id' | 'organization_id'>>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [slugManual, setSlugManual] = useState(false);

  useEffect(() => {
    if (!isNew && eventId) {
      getEventById(eventId).then((r) => {
        if (r.data) {
          const { id: _id, organization_id: _org, ...rest } = r.data;
          void _id; void _org;
          setForm(rest);
          setSlugManual(true);
        }
      });
    }
  }, [eventId, isNew]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    const val = type === 'checkbox' ? checked : type === 'number' ? Number(value) : value;

    setForm((prev) => {
      const updated = { ...prev, [name]: val };
      if (name === 'title' && !slugManual) {
        updated.slug = slugify(value);
      }
      return updated;
    });

    if (name === 'slug') setSlugManual(true);
    setErrors((prev) => ({ ...prev, [name]: '' }));
    setSaveError(null);
  };

  const validate = (): Record<string, string> => {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.slug.trim()) errs.slug = 'Slug is required';
    if (!form.start_date) errs.start_date = 'Start date is required';
    if (!form.location.trim()) errs.location = 'Location is required';
    if (!form.description.trim()) errs.description = 'Description is required';
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    const res = isNew ? await createEvent(form) : await updateEvent(eventId!, form);
    setSaving(false);
    if (res.success) {
      navigate('/admin/events');
    } else {
      setSaveError(res.error ?? 'Save failed. Please try again.');
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <Link to="/admin/events" className={styles.back}>← Back to Events</Link>
        <h1 className={styles.pageTitle}>{isNew ? 'New Event' : 'Edit Event'}</h1>
      </div>

      <form onSubmit={handleSubmit} noValidate className={styles.form}>
        <div className={styles.layout}>
          <div className={styles.main}>
            <Card padding="md">
              <h2 className={styles.sectionTitle}>Event Details</h2>
              <div className={styles.fields}>
                <div className={styles.field}>
                  <label htmlFor="title" className={styles.label}>Title <span className={styles.req}>*</span></label>
                  <input id="title" name="title" type="text" value={form.title} onChange={handleChange}
                    className={[styles.input, errors.title ? styles.inputError : ''].join(' ')}
                    placeholder="Annual Bonspiel — Mixed Doubles" />
                  {errors.title && <p className={styles.error}>{errors.title}</p>}
                </div>

                <div className={styles.field}>
                  <label htmlFor="slug" className={styles.label}>URL Slug <span className={styles.req}>*</span></label>
                  <div className={styles.slugWrapper}>
                    <span className={styles.slugPrefix}>/events/</span>
                    <input id="slug" name="slug" type="text" value={form.slug} onChange={handleChange}
                      className={[styles.input, styles.slugInput, errors.slug ? styles.inputError : ''].join(' ')}
                      placeholder="annual-bonspiel-2026" />
                  </div>
                  {errors.slug && <p className={styles.error}>{errors.slug}</p>}
                </div>

                <div className={[styles.field, styles.fullWidth].join(' ')}>
                  <label htmlFor="description" className={styles.label}>Description <span className={styles.req}>*</span></label>
                  <textarea id="description" name="description" value={form.description} onChange={handleChange} rows={6}
                    className={[styles.textarea, errors.description ? styles.inputError : ''].join(' ')}
                    placeholder="Full event description shown on the public event page…" />
                  {errors.description && <p className={styles.error}>{errors.description}</p>}
                </div>

                <div className={styles.field}>
                  <label htmlFor="location" className={styles.label}>Location <span className={styles.req}>*</span></label>
                  <input id="location" name="location" type="text" value={form.location} onChange={handleChange}
                    className={[styles.input, errors.location ? styles.inputError : ''].join(' ')}
                    placeholder="Kindersley Curling Club, Sheet 1–6" />
                  {errors.location && <p className={styles.error}>{errors.location}</p>}
                </div>

                <div className={styles.field}>
                  <label htmlFor="featured_image_url" className={styles.label}>Featured Image URL</label>
                  <input id="featured_image_url" name="featured_image_url" type="url" value={form.featured_image_url}
                    onChange={handleChange} className={styles.input} placeholder="https://…" />
                </div>
              </div>
            </Card>

            <Card padding="md">
              <h2 className={styles.sectionTitle}>Date & Time</h2>
              <div className={styles.fields}>
                <div className={styles.field}>
                  <label htmlFor="start_date" className={styles.label}>Start Date & Time <span className={styles.req}>*</span></label>
                  <input id="start_date" name="start_date" type="datetime-local"
                    value={form.start_date ? form.start_date.slice(0, 16) : ''}
                    onChange={handleChange}
                    className={[styles.input, errors.start_date ? styles.inputError : ''].join(' ')} />
                  {errors.start_date && <p className={styles.error}>{errors.start_date}</p>}
                </div>
                <div className={styles.field}>
                  <label htmlFor="end_date" className={styles.label}>End Date & Time</label>
                  <input id="end_date" name="end_date" type="datetime-local"
                    value={form.end_date ? form.end_date.slice(0, 16) : ''}
                    onChange={handleChange} className={styles.input} />
                </div>
              </div>
            </Card>

            <Card padding="md">
              <h2 className={styles.sectionTitle}>Registration</h2>
              <div className={styles.fields}>
                <div className={[styles.field, styles.fullWidth].join(' ')}>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" name="registration_open" checked={form.registration_open}
                      onChange={handleChange} className={styles.checkbox} />
                    Registration is open for this event
                  </label>
                </div>
                <div className={styles.field}>
                  <label htmlFor="registration_deadline" className={styles.label}>Registration Deadline</label>
                  <input id="registration_deadline" name="registration_deadline" type="datetime-local"
                    value={form.registration_deadline ? form.registration_deadline.slice(0, 16) : ''}
                    onChange={handleChange} className={styles.input} disabled={!form.registration_open} />
                </div>
                <div className={styles.field}>
                  <label htmlFor="max_entries" className={styles.label}>Max Entries</label>
                  <input id="max_entries" name="max_entries" type="number" min="0"
                    value={form.max_entries} onChange={handleChange} className={styles.input}
                    disabled={!form.registration_open} placeholder="0 = unlimited" />
                  <p className={styles.hint}>Set to 0 for unlimited entries</p>
                </div>
              </div>
            </Card>
          </div>

          <aside className={styles.sidebar}>
            <Card padding="md">
              <h2 className={styles.sectionTitle}>Publish</h2>
              <div className={styles.publishFields}>
                <div className={styles.field}>
                  <label htmlFor="status" className={styles.label}>Status</label>
                  <select id="status" name="status" value={form.status} onChange={handleChange} className={styles.select}>
                    {EVENT_STATUSES.map((s) => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className={styles.field}>
                  <label htmlFor="type" className={styles.label}>Event Type</label>
                  <select id="type" name="type" value={form.type} onChange={handleChange} className={styles.select}>
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                {saveError && <div className={styles.saveError}>{saveError}</div>}
                <Button type="submit" loading={saving} style={{ width: '100%' }}>
                  {isNew ? 'Create Event' : 'Save Changes'}
                </Button>
                <Link to="/admin/events" className={styles.cancelLink}>Cancel</Link>
              </div>
            </Card>
          </aside>
        </div>
      </form>
    </div>
  );
}
